import { useSelector, useIsCancellationScheduled, useIsSubscriptionCancelled } from '../../hooks/usePrerenderUser';
import { selectDues, useGetSubscriptionQuery } from './api/billingApiLegacySlice';
import { useIsPaymentInfoValidQuery } from './api/billingApiSlice';

export type PlanStatus = 'active' | 'cancellation_scheduled' | 'cancelled';

export function usePlanStatus(): PlanStatus {
  const isCancellationScheduled = useIsCancellationScheduled();
  const isPlanCancelled = useIsSubscriptionCancelled();

  if (isPlanCancelled) return 'cancelled';
  if (isCancellationScheduled) return 'cancellation_scheduled';
  return 'active';
}

type PlanState = {
  status: PlanStatus;
  recachingDisabled: boolean;
};

type PaymentMethodState = {
  isLoading: boolean;
  isChargeable: boolean; // means everything is valid and the user is able to be charged
  isCreditCardValid: boolean;
  isPaymentMethodSet: boolean; // card could be set, but not valid
  isAddressValid: boolean;
};

type SubscriptionState = {
  isLoading: boolean;
  totalDues: number;
  hasHierarchies: boolean;
};

export function useSubscriptionState(): [PlanState, PaymentMethodState, SubscriptionState] {
  const recachingDisabled = useSelector((state) => state.prerenderUser.recachingDisabled);

  const status = usePlanStatus();

  const SubscriptionResult = useGetSubscriptionQuery();
  const PaymentInfoResult = useIsPaymentInfoValidQuery();

  const isChargeable =
    PaymentInfoResult.data?.data.isCreditCardValid &&
    PaymentInfoResult.data?.data.isAddressValid &&
    PaymentInfoResult.data?.data.hasPaymentMethod;

  return [
    {
      status,
      recachingDisabled,
    },
    {
      isLoading: PaymentInfoResult.isFetching,
      isCreditCardValid: !!PaymentInfoResult.data?.data.isCreditCardValid,
      isPaymentMethodSet: !!PaymentInfoResult.data?.data.hasPaymentMethod,
      isAddressValid: !!PaymentInfoResult.data?.data.isAddressValid,
      isChargeable: !!isChargeable,
    },
    {
      isLoading: SubscriptionResult.isFetching,
      ...selectDues(SubscriptionResult.data),
    },
  ];
}
